import { ActionPayload, DataLakeDirectoryListPayload, DataLakeDirectoryModel, DataLakeFileSystemModel, DataLakeFileDownloadResponse, DataLakeFileModel } from "../../gql-types.generated";
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../store';

interface DownloadActionPayload extends ActionPayload {
    response?: DataLakeFileDownloadResponse;
}

interface SliceState {
    error?: Error;
    actionError?: Error;
    fetchDirectoriesStatus?: DataLakeDirectoryListPayload;
    fetchDirectoryHierarchyStatus?: DataLakeDirectoryListPayload;
    rootDirectories?: DataLakeFileSystemModel;
    directoryHierarchy?: DataLakeFileSystemModel;
    activeDirectory?: DataLakeDirectoryModel;
    fileSearchResults?: DataLakeFileModel[];
    downloadFileStatus?: DownloadActionPayload;
    uploadFileStatus?: ActionPayload;
    deleteFileStatus?: ActionPayload;
    moveFileStatus?: ActionPayload;
    reprocessFileStatus?: ActionPayload;
    createDirectoryStatus?: ActionPayload;
    deleteDirectoryStatus?: ActionPayload;
    isDirectoryLoading: boolean;
    isSearchLoading: boolean;
}

const initialState: SliceState = {
    error: undefined,
    actionError: undefined,
    fetchDirectoriesStatus: undefined,
    fetchDirectoryHierarchyStatus: undefined,
    rootDirectories: undefined,
    directoryHierarchy: undefined,
    activeDirectory: undefined,
    fileSearchResults: undefined,
    downloadFileStatus: undefined,
    uploadFileStatus: undefined,
    deleteFileStatus: undefined,
    moveFileStatus: undefined,
    reprocessFileStatus: undefined,
    createDirectoryStatus: undefined,
    deleteDirectoryStatus: undefined,
    isDirectoryLoading: false,
    isSearchLoading: false,
};

export const slice = createSlice({
    name: 'fileManager',
    initialState,
    reducers: {
        fetchError: (state, action: PayloadAction<Error>) => {
            // eslint-disable-next-line no-param-reassign
            state.error = action.payload;
        },

        fetchActionError: (state, action: PayloadAction<Error>) => {
            // eslint-disable-next-line no-param-reassign
            state.actionError = action.payload;
        },

        clearError: state => {
            // eslint-disable-next-line no-param-reassign
            state.error = undefined;
        },

        clearActionError: state => {
            // eslint-disable-next-line no-param-reassign
            state.actionError = undefined;
        },

        clearCurrentDirectoryHierarchy: state => {
            // eslint-disable-next-line no-param-reassign
            state.directoryHierarchy = undefined;
        },

        clearState: state => {
            // eslint-disable-next-line no-param-reassign
            state.error = undefined;
            // eslint-disable-next-line no-param-reassign
            state.actionError = undefined;
            // eslint-disable-next-line no-param-reassign
            state.fetchDirectoriesStatus = undefined;
            // eslint-disable-next-line no-param-reassign
            state.fetchDirectoryHierarchyStatus = undefined;
            // eslint-disable-next-line no-param-reassign
            state.rootDirectories = undefined;
            // eslint-disable-next-line no-param-reassign
            state.directoryHierarchy = undefined;
            // eslint-disable-next-line no-param-reassign
            state.activeDirectory = undefined;
            // eslint-disable-next-line no-param-reassign
            state.fileSearchResults = undefined;
            // eslint-disable-next-line no-param-reassign
            state.downloadFileStatus = undefined;
            // eslint-disable-next-line no-param-reassign
            state.uploadFileStatus = undefined;
            // eslint-disable-next-line no-param-reassign
            state.deleteFileStatus = undefined;
            // eslint-disable-next-line no-param-reassign
            state.moveFileStatus = undefined;
            // eslint-disable-next-line no-param-reassign
            state.reprocessFileStatus = undefined;
            // eslint-disable-next-line no-param-reassign
            state.createDirectoryStatus = undefined;
            // eslint-disable-next-line no-param-reassign
            state.deleteDirectoryStatus = undefined;
            // eslint-disable-next-line no-param-reassign
            state.isDirectoryLoading = false;
        },

        fetchFileDirectoriesSuccess: (state, action: PayloadAction<DataLakeDirectoryListPayload>) => {
            if (action.payload) {  
                let directoriesJson = action.payload.directoryHierarchyJson;
                if (directoriesJson) {
                    //console.log('JSON directory hierarchy:', directoriesJson);
                        
                    // eslint-disable-next-line no-param-reassign
                    state.fetchDirectoriesStatus = action.payload;
                    
                    try {
                        let directories = JSON.parse(directoriesJson);
                        //console.log('Parsed directory hierarchy:', directories);
                        
                        if (!directories || typeof directories !== 'object') {
                            throw new Error('Invalid directory data format');
                        }

                        // eslint-disable-next-line no-param-reassign
                        state.rootDirectories = directories as DataLakeFileSystemModel;
                    } catch (e) {
                        console.error('Failed to parse directory data:', e);
                        // eslint-disable-next-line no-param-reassign
                        state.error = e as Error;
                    }
                }
            }
        },

        fetchDirectoryHierarchySuccess: (state, action: PayloadAction<DataLakeDirectoryListPayload>) => {
            if (action.payload) {  
                let directoriesJson = action.payload.directoryHierarchyJson;
                if (directoriesJson) {
                    //console.log('JSON directory hierarchy:', directoriesJson);
                        
                    // eslint-disable-next-line no-param-reassign
                    state.fetchDirectoryHierarchyStatus = action.payload;
                    
                    try {
                        let directories = JSON.parse(directoriesJson);
                        //console.log('Parsed directory hierarchy:', directories);
                        
                        if (!directories || typeof directories !== 'object') {
                            throw new Error('Invalid directory data format');
                        }

                        // eslint-disable-next-line no-param-reassign
                        state.directoryHierarchy = directories as DataLakeFileSystemModel;
                    } catch (e) {
                        console.error('Failed to parse directory data:', e);
                        // eslint-disable-next-line no-param-reassign
                        state.error = e as Error;
                    }
                }
            }
        },

        fetchFileSearchFilesSuccess: (state, action: PayloadAction<DataLakeFileModel[] | undefined>) => {
            if (action.payload) {
                // eslint-disable-next-line no-param-reassign  
                state.fileSearchResults = action.payload;
            } else {
                // eslint-disable-next-line no-param-reassign
                state.fileSearchResults = undefined;
            }
        },

        setDirectoryLoading: (state) => {
            // eslint-disable-next-line no-param-reassign
            state.isDirectoryLoading = true;
            // eslint-disable-next-line no-param-reassign
            state.activeDirectory = undefined;
        },

        setSearchLoading: (state) => {
            // eslint-disable-next-line no-param-reassign
            state.isSearchLoading = true;
        },

        clearSearchLoading: (state) => {
            // eslint-disable-next-line no-param-reassign
            state.isSearchLoading = false;
        },

        fetchDirectoryDetailsSuccess: (state, action: PayloadAction<DataLakeDirectoryModel>) => {
            if (action.payload) {  
                // eslint-disable-next-line no-param-reassign
                state.activeDirectory = action.payload;
                // eslint-disable-next-line no-param-reassign
                state.isDirectoryLoading = false;
            }
        },

        captureDownloadFileStatus: (state, action: PayloadAction<ActionPayload | undefined>) => {
            if (action.payload?.data) {
                try {
                    const downloadResponse = JSON.parse(action.payload.data) as DataLakeFileDownloadResponse;
                    // Create new DownloadActionPayload combining original ActionPayload and parsed response
                    const downloadPayload: DownloadActionPayload = {
                        ...action.payload,
                        response: downloadResponse
                    };
                    // eslint-disable-next-line no-param-reassign
                    state.downloadFileStatus = downloadPayload;
                } catch (e) {
                    console.error('Failed to parse download response:', e);
                    // eslint-disable-next-line no-param-reassign
                    state.error = e as Error;
                }
            } else {
                // eslint-disable-next-line no-param-reassign
                state.downloadFileStatus = undefined;
            }
        },

        captureUploadFileStatus: (state, action: PayloadAction<ActionPayload | undefined>) => {
            if (action.payload?.data) {
                // eslint-disable-next-line no-param-reassign
                state.uploadFileStatus = action.payload;
            } else {
                // eslint-disable-next-line no-param-reassign
                state.uploadFileStatus = undefined;
            }
        },

        captureCreateDirectoryStatus: (state, action: PayloadAction<ActionPayload | undefined>) => {
            if (action.payload?.data) {
                // eslint-disable-next-line no-param-reassign
                state.createDirectoryStatus = action.payload;
            } else {
                // eslint-disable-next-line no-param-reassign
                state.createDirectoryStatus = undefined;
            }
        },

        captureDeleteDirectoryStatus: (state, action: PayloadAction<ActionPayload | undefined>) => {
            // eslint-disable-next-line no-param-reassign
            state.deleteDirectoryStatus = action.payload;
        },

        captureDeleteFileStatus: (state, action: PayloadAction<ActionPayload | undefined>) => {
            // eslint-disable-next-line no-param-reassign
            state.deleteFileStatus = action.payload;
        },

        captureMoveFileStatus: (state, action: PayloadAction<ActionPayload | undefined>) => {
            // eslint-disable-next-line no-param-reassign
            state.moveFileStatus = action.payload;
        },

        captureReprocessFileStatus: (state, action: PayloadAction<ActionPayload | undefined>) => {
            // eslint-disable-next-line no-param-reassign
            state.reprocessFileStatus = action.payload;
        }
    }
});

export const selectError = (state: RootState): Error | undefined => state.fileManager.error;
export const selectActionError = (state: RootState): Error | undefined => state.fileManager.actionError;
export const selectRootDirectories = (state: RootState): DataLakeFileSystemModel | undefined => state.fileManager.rootDirectories;
export const selectDirectoryHierarchy = (state: RootState): DataLakeFileSystemModel | undefined => state.fileManager.directoryHierarchy;
export const selectFetchDirectoriesStatus = (state: RootState): DataLakeDirectoryListPayload | undefined => state.fileManager.fetchDirectoriesStatus;
export const selectFetchDirectoryHierarchyStatus = (state: RootState): DataLakeDirectoryListPayload | undefined => state.fileManager.fetchDirectoryHierarchyStatus;
export const selectFileSearchResultFiles = (state: RootState): DataLakeFileModel[] | undefined => state.fileManager.fileSearchResults;
export const selectActiveDirectory = (state: RootState): DataLakeDirectoryModel | undefined => state.fileManager.activeDirectory;
export const selectDownloadFileStatus = (state: RootState): DownloadActionPayload | undefined => state.fileManager.downloadFileStatus;
export const selectUploadFileStatus = (state: RootState): ActionPayload | undefined => state.fileManager.uploadFileStatus;
export const selectCreateDirectoryStatus = (state: RootState): ActionPayload | undefined => state.fileManager.createDirectoryStatus;
export const selectDeleteDirectoryStatus = (state: RootState): ActionPayload | undefined => state.fileManager.deleteDirectoryStatus;
export const selectDeleteFileStatus = (state: RootState): ActionPayload | undefined => state.fileManager.deleteFileStatus;
export const selectMoveFileStatus = (state: RootState): ActionPayload | undefined => state.fileManager.moveFileStatus;
export const selectReprocessFileStatus = (state: RootState): ActionPayload | undefined => state.fileManager.reprocessFileStatus;
export const selectIsDirectoryLoading = (state: RootState): boolean => state.fileManager.isDirectoryLoading;
export const selectIsSearchLoading = (state: RootState): boolean => state.fileManager.isSearchLoading;

export const {
    fetchError,
    fetchActionError,
    clearActionError,
    clearError,
    clearState,
    clearCurrentDirectoryHierarchy,
    fetchFileDirectoriesSuccess,
    fetchDirectoryHierarchySuccess,
    fetchDirectoryDetailsSuccess,
    fetchFileSearchFilesSuccess,
    captureDownloadFileStatus,
    captureUploadFileStatus,
    captureCreateDirectoryStatus,
    captureDeleteDirectoryStatus,
    captureDeleteFileStatus,
    captureMoveFileStatus,
    captureReprocessFileStatus,
    setDirectoryLoading,
    setSearchLoading,
    clearSearchLoading
} = slice.actions;

export default slice.reducer;
